/**
 * @fileoverview Section "Menus" — DropdownMenu, Tabs et LanguageSwitcher GNPC.
 *
 * Montre les menus d'actions backoffice (actions simples, sous-menu,
 * checkbox items, radio items, état destructif, raccourcis clavier), la
 * navigation par onglets Tabs et le sélecteur de langue du portail.
 */

import { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  LanguageSwitcher,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@gnpc/ui';
import { Copy, Download, Eye, MoreHorizontal, RefreshCcw, ShieldAlert } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

const DS_LANGUAGES = [
  { code: 'fr', shortLabel: 'FR', nativeName: 'Français' },
  { code: 'en', shortLabel: 'EN', nativeName: 'English' },
  { code: 'es', shortLabel: 'ES', nativeName: 'Español' },
  { code: 'de', shortLabel: 'DE', nativeName: 'Deutsch' },
] as const;

export function DsSectionMenus(): JSX.Element {
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const [density, setDensity] = useState('comfortable');
  const [language, setLanguage] = useState<(typeof DS_LANGUAGES)[number]['code']>('fr');

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <DsPreview label="DropdownMenu — actions paiement">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <MoreHorizontal />
              Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Paiement PAY-2947</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Eye />
                Voir le détail
                <DropdownMenuShortcut>↵</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Copy />
                Copier la référence
                <DropdownMenuShortcut>⌘C</DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Download />
                Télécharger le reçu
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <RefreshCcw />
                Rejouer STI
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                <DropdownMenuItem>Rejouer maintenant</DropdownMenuItem>
                <DropdownMenuItem>Planifier un rejeu</DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive">
              <ShieldAlert />
              Signaler une anomalie
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </DsPreview>

      <DsPreview label="Checkbox + radio — préférences de vue">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">Préférences table</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Filtres rapides</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={showFailedOnly}
              onCheckedChange={setShowFailedOnly}
            >
              Voir seulement les échecs STI
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Densité</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={density} onValueChange={setDensity}>
              <DropdownMenuRadioItem value="compact">Compacte</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="comfortable">Confortable</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="spacious">Aérée</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="mt-4 rounded-md bg-muted p-2 font-mono text-[11px] text-quipux-navy-800">
          failedOnly={String(showFailedOnly)} · density={density}
        </div>
      </DsPreview>

      <DsPreview label="Tabs — vues d'un même objet métier">
        <Tabs defaultValue="details" className="w-full">
          <TabsList>
            <TabsTrigger value="details">Détail</TabsTrigger>
            <TabsTrigger value="timeline">Chronologie</TabsTrigger>
            <TabsTrigger value="sti">Logs STI</TabsTrigger>
          </TabsList>
          <TabsContent value="details">
            <p className="text-muted-foreground pt-3 text-sm">
              Informations principales du paiement : montant, opérateur, références.
            </p>
          </TabsContent>
          <TabsContent value="timeline">
            <p className="text-muted-foreground pt-3 text-sm">
              Transitions financières et STI, du plus ancien au plus récent.
            </p>
          </TabsContent>
          <TabsContent value="sti">
            <p className="text-muted-foreground pt-3 text-sm">
              Tentatives d'enregistrement auprès du STI avec leur statut.
            </p>
          </TabsContent>
        </Tabs>
      </DsPreview>

      <DsPreview label="LanguageSwitcher — portail">
        <div className="flex flex-wrap items-center gap-4">
          <LanguageSwitcher
            value={language}
            options={DS_LANGUAGES}
            onValueChange={setLanguage}
            surface="light"
          />
          <div className="bg-quipux-navy-950 rounded-lg p-3">
            <LanguageSwitcher
              value={language}
              options={DS_LANGUAGES}
              onValueChange={setLanguage}
              surface="dark"
            />
          </div>
        </div>
        <p className="text-muted-foreground mt-4 text-sm">
          UI-only : ce composant expose le choix, l'application décide du stockage et de la
          traduction éventuelle.
        </p>
      </DsPreview>
    </div>
  );
}
